// One definition of "this quote is still waiting to be priced", shared by the row badge
// and the "Awaiting Pricing" tile on QuoteRequested.
//
// Reads `pricing_log_status` — D1 `deal_pricing_logs.status`, enriched onto each deal in
// workers/src/routes/deals.js — and deliberately NOT `claude_pricing_log`.
//
// The tile used to test `!claude_pricing_log`, which treats any non-empty string as proof
// of pricing. That includes '[REQUEUED] operator forced a reprice', the text
// writePricingStatus writes when an operator forces a reprice — so a just-requeued quote
// counted as priced and vanished from the tile while its own row still read "Awaiting
// pricing" (live on TI-32A2F / deal 513212843236, 2026-07-27). Status has no such failure
// mode: a requeue sets it to 'pending'.
//
// Each excluded status already has its own badge — 'completed' shows nothing (or "Needs
// review" / "No price"), 'error' shows "Pricing failed", 'retrying' shows "Retrying…". So
// this list is exactly "the statuses that are not the Awaiting pricing badge", which is
// what keeps the two in step.
//
// Exclusion rather than a match on 'pending' because a deal with no D1 row at all has an
// undefined status: nothing has priced it, so it is awaiting pricing.
const NOT_AWAITING = ['completed', 'error', 'retrying']

export const isAwaitingPricing = (deal) => !NOT_AWAITING.includes(deal?.pricing_log_status)

// `parsedItems` are parseItemLine shapes (sp/bp strings, '0' when absent).
const everyItemPriced = (parsedItems) =>
  Array.isArray(parsedItems) && parsedItems.length > 0
  && parsedItems.every(i => Number(i?.sp) > 0 || Number(i?.bp) > 0)

// D1 says 'pending' but every item already carries a price. The engine cannot leave this
// state on its own: G1 only offers quotes with an unpriced item, so nothing will ever
// revisit the row, and 'pending' is what disables Force Re-price ("Already queued").
// Reached when two engine runs overlapped and the loser's "partial" verdict overwrote the
// winner's 'completed' (TI-7405407E, 2026-08-18 — the worker now holds its claim through
// the writeback, so new cases should not arise; existing rows still need a way out).
//
// Deliberately 'pending' only: a fresh requeue clears the tiers first (nothing priced, so
// not stale), and a real partial has an unpriced item (the feed re-offers it).
export const isPricedButStale = (deal, parsedItems) =>
  deal?.pricing_log_status === 'pending' && everyItemPriced(parsedItems)
